import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import {
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	Validators,
} from '@angular/forms';
import { ageValidator } from '@webapp/core/helpers/age.validator';
import {
	ButtonComponent,
	FormUtilitiesService,
	IconComponent,
	InputComponent,
	InputErrorComponent,
	LoadingComponent,
	ToastService,
} from '@webapp/ui';
import { NgxMaskDirective } from 'ngx-mask';
import { CuitBlurNormalizeDirective, cuitValidator } from '@webapp/core';

import { UsersService } from '@webapp/users/services/users.service';
import { ZonesService } from '@webapp/core/services/zones.service';
import { firstValueFrom } from 'rxjs';
import { IBasePolicy } from '@webapp/policies';
import { IUser } from '@webapp/auth';
import { IZone } from '@webapp/core/interfaces/zones.interface';
import { InputAddressComponent } from '@webapp/core/ui/components/input-address/input-address.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

@Component({
	selector: 'app-client-info-endorsement-form',
	imports: [
    CommonModule,
    IconComponent,
    InputComponent,
    InputErrorComponent,
    ReactiveFormsModule,
    ButtonComponent,
    InputAddressComponent,
    LoadingComponent,
    MatAutocompleteModule,
    NgxMaskDirective,
    CuitBlurNormalizeDirective,
],
	standalone: true,
	templateUrl: './client-info-endorsement-form.component.html',
	styleUrl: './client-info-endorsement-form.component.scss',
})
export class ClientInfoEndorsementFormComponent implements OnInit {
	closed = output<void>();
	policy = input<IBasePolicy>();
	user = input<IUser>();
	originalUserData: Partial<IUser> = {};

	form: FormGroup = new FormGroup({
		firstName: new FormControl(this.user()?.firstName),
		lastName: new FormControl(this.user()?.lastName),
		companyName: new FormControl(this.user()?.companyName),
		identification: new FormControl(this.user()?.identification),
		birthDate: new FormControl(this.user()?.birthDate),
		address: new FormControl(this.user()?.address),
		postalCode: new FormControl(this.user()?.postalCode),
		zoneId: new FormControl(''),
	});

	isSubmitted = false;

	zones = signal<IZone[]>([]);
	selectedZone = signal('');
	checkingPostalCode = signal(false);
	isLoading = signal(false);
	hasPostalCodeChanged = signal(false);

	isCompany = computed(() => {
		const userData = this.user();
		return !!(userData?.companyName);
	});

	formUtilities = inject(FormUtilitiesService);
	toastService = inject(ToastService);
	usersService = inject(UsersService);
	zonesService = inject(ZonesService);

	ngOnInit(): void {
		if (this.user()) {
			this.setUser(this.user() ?? {})
		}
		this.configureValidatorsByUserType();
	}

	private configureValidatorsByUserType(): void {
		const identification = this.form.get('identification');
		const birthDate = this.form.get('birthDate');

		if (this.isCompany()) {
			this.form.get('companyName')?.setValidators([Validators.required]);
			identification?.setValidators([Validators.required, cuitValidator]);
			birthDate?.setValidators([]);
		} else {
			this.form.get('firstName')?.setValidators([Validators.required]);
			this.form.get('lastName')?.setValidators([Validators.required]);
			identification?.setValidators([Validators.required]);
			birthDate?.setValidators([Validators.required, ageValidator]);
		}

		identification?.updateValueAndValidity({ emitEvent: false });
		birthDate?.updateValueAndValidity({ emitEvent: false });
	}

	setUser(user: IUser): void {
		const userMapped = {
			firstName: user.firstName,
			lastName: user.lastName,
			companyName: user.companyName,
			identification: user.identification,
			birthDate: user.birthDate,
			address: user.address,
			postalCode: user.postalCode,
			zoneId: ''
		}
		this.originalUserData = { ...userMapped };
		this.form.reset({
			firstName: user.firstName,
			lastName: user.lastName,
			companyName: user.companyName,
			identification: user.identification,
			birthDate: user.birthDate,
			address: user.address,
			postalCode: user.postalCode,
			zoneId: ''
		});
	}

	isModified(): boolean {
		return JSON.stringify(this.form.getRawValue()) !== JSON.stringify(this.originalUserData);
	}


	async onSubmit(): Promise<void> {
		this.isSubmitted = true;
		if (!this.form.valid) return;

		if (this.zones().length > 1 && !this.selectedZone()) {
			this.toastService.showToast(
				'Zona requerida',
				'Debe seleccionar una zona para el código postal ingresado.',
				'error'
			);
			return;
		}
		this.isLoading.set(true);

		const response = await firstValueFrom(this.usersService.updateUser(this.user()?.id ?? '', this.getModifiedFields()));
		if (response) {
			this.isLoading.set(false);
			this.toastService.showToast(
				'Modificación realizada',
				'La modificación de la información del cliente se ha realizado correctamente.',
				'success'
			);
			
			this.closed.emit();
		}
		

	}

	getModifiedFields(): Partial<IUser> {
		const current = this.form.getRawValue();
		const original = this.originalUserData;
		const modified: Partial<IUser> = {};

		Object.keys(current).forEach((key) => {
			const currentValue = current[key as keyof IUser];
			const originalValue = original[key as keyof IUser];

			if (JSON.stringify(currentValue) !== JSON.stringify(originalValue)) {
				modified[key as keyof IUser] = currentValue;
			}
		});

		return modified;
	}


	checkPostalCode() {
		const postalCode = this.form.get('postalCode')?.value;
		if (!postalCode) return;
		this.hasPostalCodeChanged.set(true);
		this.checkingPostalCode.set(true);
		this.zonesService.getZones(postalCode).subscribe({
			next: (response) => {
				this.checkingPostalCode.set(false);
				const zones = response;
				if (zones.length === 1) {
					this.form.get('zoneId')?.setValue(zones[0].id); 
					this.selectedZone.set(zones[0].id);
					this.zones.set([]);
				} else {
					this.zones.set(zones);
					this.selectedZone.set('');
				}
			},
			error: () => {
				this.checkingPostalCode.set(false);
			},
		});
	}

	selectZone(zoneId: string) {
		this.selectedZone.set(zoneId);
	}

	onZoneSelect(event: Event) {
		const target = event.target as HTMLSelectElement;
		this.selectZone(target.value);
	}


	onCancel(): void {
		this.closed.emit();
	}

}
